import { useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../button/button';
import { Input } from '../input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select';
import type { ConnectionPlatform } from '../../../lib/hooks/useConnectionRequests';

type Request = (platform: ConnectionPlatform, note?: string) => Promise<void>;

export const OTHER_PLATFORMS: { value: ConnectionPlatform; label: string }[] = [
  { value: 'aws_bedrock', label: 'AWS Bedrock' },
  { value: 'vertex_ai', label: 'Vertex AI' },
  { value: 'openai_assistants', label: 'OpenAI Assistants' },
  { value: 'copilot_studio', label: 'Copilot Studio' },
  { value: 'other', label: 'Something else' },
];

async function send(action: () => Promise<void>, success: string) {
  try {
    await action();
    toast.success(success);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Try again.');
  }
}

/** Which platform someone wants next, so we build what's asked for. */
export function PlatformRequestForm({
  requested,
  onRequest,
}: {
  requested: Set<ConnectionPlatform>;
  onRequest: Request;
}) {
  const [platform, setPlatform] = useState<ConnectionPlatform | ''>('');
  const [note, setNote] = useState('');
  const [isSending, setIsSending] = useState(false);

  const requestedLabels = OTHER_PLATFORMS.filter((option) =>
    requested.has(option.value),
  ).map((option) => option.label);

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!platform) return;
        setIsSending(true);
        await send(
          () => onRequest(platform, note),
          "Thanks, we'll let you know",
        );
        setPlatform('');
        setNote('');
        setIsSending(false);
      }}
    >
      <div className="flex gap-2">
        <Select
          value={platform}
          onValueChange={(value) => setPlatform(value as ConnectionPlatform)}
        >
          <SelectTrigger
            aria-label="Platform"
            className="bg-prompt-safecomwhite"
          >
            <SelectValue placeholder="Which platform?" />
          </SelectTrigger>
          <SelectContent>
            {OTHER_PLATFORMS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="submit"
          variant="outline"
          disabled={!platform || isSending}
        >
          Notify me
        </Button>
      </div>
      {platform === 'other' && (
        <Input
          aria-label="Which platform"
          placeholder="Which one?"
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          className="bg-prompt-safecomwhite"
        />
      )}
      {requestedLabels.length > 0 && (
        <RequestedNote>
          You asked for {requestedLabels.join(', ')}
        </RequestedNote>
      )}
    </form>
  );
}

function RequestedNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 text-sm text-green-800">
      <Check className="h-4 w-4 flex-none" />
      {children}
    </p>
  );
}
